<template>
    <div>
        <v-container fluid>
            <v-card class="pb-3 mb-3">
                <v-card-title class="d-flex justify-center ">{{ title }}</v-card-title>
                <v-row justify="center" align="center" v-if="loading">
                    <div class="text-center">
                    <div>
                        <v-progress-circular
                        class="my-4"
                        :size="50"
                        width="5"
                        color="primary"
                        indeterminate
                        ></v-progress-circular>
                    </div>
                    <p class="display-1">{{ $t("Loading") }}...</p>
                    </div>
                </v-row>
                <v-tabs centered v-model="selectedTab" v-if="!loading">
                <v-tab>{{ $t("Description") }}</v-tab>
                <v-tab>{{ $t("FunctionRights") }}</v-tab>
                <v-tab>{{ $t("Users") }}</v-tab>
                <v-tab-item>
                    <v-card flat class="my-0">
                    <v-card-text>
                        <v-form class="mt-0">
                        <v-text-field
                            v-bind:label="$t('Name')"
                            v-model="role.name"
                            maxlength="100"
                            :counter="100"
                            :error-messages="nameErrors"
                            @input="$v.role.name.$touch()"
                            @blur="$v.role.name.$touch()"
                        ></v-text-field>

                        <v-text-field
                            v-bind:label="$t('Description')"
                            v-model="role.description"
                            maxlength="100"
                            :counter="100"
                            :error-messages="descriptionErrors"
                            @input="$v.role.description.$touch()"
                            @blur="$v.role.description.$touch()"
                        ></v-text-field>
                        </v-form>
                    </v-card-text>
                    </v-card>
                </v-tab-item>
                <v-tab-item>
                    <v-card class="my-0">
                    <v-card-text>
                        <v-row>
                        <BaseDualList
                            class="ml-3 mr-3"
                            :source="availablesFunctions"
                            :destination="role.functionRights"
                            item-text="text"
                            item-id="id"
                            @onChangeList="onFunctionRightListChange"
                        />
                        </v-row>
                    </v-card-text>
                    </v-card>
                </v-tab-item>
                <v-tab-item>
                    <v-card class="my-0">
                    <v-card-text>
                        <v-row>
                        <BaseDualList
                            class="mr-3 ml-3"
                            :source="availablesUsers"
                            :destination="role.users"
                            item-text="text"
                            item-id="id"
                            @onChangeList="onUserListChange"
                            data-testid="role-user-list"
                        />
                        </v-row>
                    </v-card-text>
                    </v-card>
                </v-tab-item>
                </v-tabs>

                <v-card-text>
                    <v-row v-if="!loading" justify="end">
                        <v-btn @click="close">
                            {{ $t("Cancel") }}
                        </v-btn>
                        <v-btn
                            color="primary"
                            @click.prevent="saveItem"
                            :loading="saving"
                        >
                            {{ $t("Save") }}
                        </v-btn>
                    </v-row>
                </v-card-text>

            </v-card>
        </v-container>
    </div>
</template>
<script>
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

import RoleService from '@/services/RoleService';
import Role from '@/ecm/models/Role';
import Utils from '@/ecm/common/Utils';
import AccessRightsAdministration from '@/views/administration/contentType/rights/AccessRightsAdministration.vue';
import Basket from '@/views/administration/basket/Basket.vue';

export default {
    name: 'Role',
    components: {
        AccessRightsAdministration,
        Basket
    },
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            vm.componentFrom = from;
        });
    },
    async created() {
        this.$v.$reset();
        this.roleId = this.$route.params.id;

        this.loading = true;
        try {
            this.userList = await RoleService.getUserList(this.roleId);
            const data = await RoleService.getFunctionRightList();
            this.functionRightsList = data.map((item) => ({
                ...item,
                text: item.text,
            }));
            if (this.roleId > 0) {
                this.loadRole();
            } else {
                this.new();
            }

            this.$v.$reset();
        } finally {
            this.loading = false;
        }
    },
    data() {
        return {
            userList: [],
            availablesUsers: [],
            functionRightsList: [],
            availablesFunctions: [],
            open: false,
            role: new Role(),
            selectedTab: 0,
            isNew: true,
            loading: false,
            saving: false,
            roleId: 0,
            componentFrom: null,
        };
    },
    mixins: [validationMixin],
    validations: {
        role: {
            name: { required },
            description: { required },
        },
    },
    computed: {
        descriptionErrors() {
            const errors = [];
            if (!this.$v.role.description.$dirty) return errors;
            !this.$v.role.description.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        nameErrors() {
            const errors = [];
            if (!this.$v.role.name.$dirty) return errors;
            !this.$v.role.name.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        title() {
            return this.$t('Role') + (this.role.name ? ' - ' + this.role.name : '');
        }
    },
    methods: {
        onFunctionRightListChange: function ({ destination, source }) {
            this.availablesFunctions = source;
            this.role.functionRights = destination;
        },
        onUserListChange: function ({ destination, source }) {
            this.availablesUsers = source;
            this.role.users = destination;
        },
        saveItem() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                this.selectedTab = 0;
                return;
            }
            if (!this.IsValid()) {
                this.selectedTab = 2;
                return;
            }

            this.saving = true;
            RoleService.save(this.role.toDto())
                .then((result) => {
                    if (result) {
                        if (this.isNew) {
                            Utils.toast.success(
                                this.$t('Role') + ' ' + this.$t('CreatedSuccessfully')
                            );
                        } else {
                            Utils.toast.success(
                                this.$t('Role') + ' ' + this.$t('UpdatedSuccessfully')
                            );
                        }
                    }
                    this.close();
                })
                .finally(() => {
                    //always
                    this.saving = false;
                });
        },
        IsValid() {
            if (this.role.users.length == 0) {
                Utils.toast.error(this.$t('ItemsSelectionError'));
                return false;
            }
            return true;
        },
        new() {
            this.role = new Role();
            this.availablesUsers = this.userList;
            this.availablesFunctions = this.functionRightsList;
            this.$v.$reset();
        },
        loadRole() {
            this.loading = true;
            RoleService.getById(this.roleId).then((dto) => {
                this.role = new Role(dto);

                this.isNew = false;
                var availablesFunctions = [...this.functionRightsList];
                var availablesUsers = [...this.userList];

                for (let i = 0; i < this.role.functionRights.length; i++) {
                    const selected = this.functionRightsList.find(
                        (functionRight) => functionRight.id === this.role.functionRights[i].id
                    );
                    this.role.functionRights[i].text = selected.text;

                    availablesFunctions = availablesFunctions.filter((x) => {
                        return x.id != selected.id;
                    });
                }

                for (let i = 0; i < this.role.users.length; i++) {
                    const selected = this.userList.find(
                        (user) => user.id == this.role.users[i].id
                    );

                    this.role.users[i].text = selected.text;

                    availablesUsers = availablesUsers.filter((x) => {
                        return x.id != selected.id;
                    });
                }

                this.availablesFunctions = availablesFunctions;
                this.availablesUsers = availablesUsers;

                this.selectedTab = 0;
                this.loading = false;
            }).catch(() => {
                this.close();
            });
        },
        close() {
            switch (this.componentFrom.name) {
            case Basket.name:
                this.$router.push(this.componentFrom.path);
                break;
            case AccessRightsAdministration.name:
                this.$router.push(this.componentFrom.path);
                break;
            default:
                this.$router.push({ name: 'RoleList' });
            }
        }
    },
};
</script>
